import { Text } from "@dropbox/dig-components/typography";
import { withShade } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import { Link } from "@mirage/mosaics/BaseProfilePage/Link";
import { ReportCountBadge } from "@mirage/mosaics/BaseProfilePage/ReportCountBadge";
import { UserProfileAvatar } from "@mirage/mosaics/UserProfilePage/UserProfileAvatar";
import type { BasicUserInfo } from "@mirage/mosaics/UserProfilePage/types";
import { getUserProfilePageHref } from "@mirage/mosaics/UserProfilePage/utils";
import cx from "classnames";
import { useCallback } from "react";
import type { LinkProps } from "react-router-dom";

import styles from "./UserProfileCard.module.css";

interface ProfileCardProps {
  user: BasicUserInfo;
  target?: LinkProps["target"];
  peopleType:
    | "peers"
    | "reports"
    | "new_arrivals"
    | "work_anniversaries"
    | "birthdays";
  featureLine: "people";
}

export const UserProfileCard = ({
  user,
  target,
  peopleType,
  featureLine,
}: ProfileCardProps) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const onLinkClick = useCallback(() => {
    // Teams are not yet configured in PAP, but the data is funneled through up
    // to this point.
    if (
      featureLine === "people" &&
      (peopleType === "peers" || peopleType === "reports")
    ) {
      reportPapEvent(
        PAP_Click_DashProfilePage({
          dashActionSurface: "dash_profile_page",
          dashProfileAction: peopleType,
          featureLine: featureLine,
        }),
      );
    }
  }, [reportPapEvent, peopleType, featureLine]);

  const isDeactivated = !!user.is_deactivated;
  const directReportsCount = user.direct_report_count ?? 0;
  const totalReportsCount = user.total_report_count ?? 0;

  return (
    <>
      <Link
        to={getUserProfilePageHref(user.display_handle)}
        hasNoUnderline
        target={target}
        onClick={onLinkClick}
        {...withShade({
          className: cx(styles.profileContainer, {
            [styles.deactivatedContainer]: isDeactivated,
          }),
        })}
      >
        <div className={styles.avatarContainer}>
          <UserProfileAvatar user={user} size="customize_large" />
          {directReportsCount && directReportsCount > 0 && totalReportsCount ? (
            <ReportCountBadge
              direct_report_count={directReportsCount}
              total_report_count={totalReportsCount}
            />
          ) : (
            <></>
          )}
        </div>
        <div className={styles.textContainer}>
          <Text
            isBold
            variant="label"
            className={cx({
              [styles.deactivatedName]: isDeactivated,
            })}
          >
            {user.name}
          </Text>
          <Text
            size="small"
            color="faint"
            variant="label"
            className={cx({
              [styles.deactivatedName]: isDeactivated,
            })}
          >
            {user.role}
          </Text>
        </div>
      </Link>
    </>
  );
};

export default UserProfileCard;
